// Public marketing landing page (rendered at / for unauthenticated visitors).
// Content is the Founding Member pitch; sign-in remains the primary CTA for
// invited members. Privacy/Terms links live in the footer for easy reach.

import ApplicationForm from './ApplicationForm.jsx';
import Slideshow from './Slideshow.jsx';
import PhotoGallery from './PhotoGallery.jsx';
import InstagramFeed from './InstagramFeed.jsx';

const EXPERIENCE = [
  {
    icon: '🌲',
    title: 'Family Adventures',
    items: [
      "Kids' Nature Playscape",
      'Hiking & Walking Trails',
      'Creek Exploration',
      'Campfire Gathering Areas',
      'Outdoor Games',
      'Open Recreation Areas',
      'Seasonal Family Activities',
      'Member Gatherings',
    ],
  },
  {
    icon: '🐎',
    title: 'Outdoor Recreation',
    items: [
      'Access to Private Horse Trails',
      'Access to Private ATV Trails',
      'Hunting Opportunities',
      'Skeet Shooting',
      'Wildlife Watching',
      'Beautiful Kentucky Countryside',
    ],
  },
];

const FOUNDING_PERKS = [
  'Founding Member Recognition',
  'Lifetime Locked-In Membership Pricing (while membership remains active)',
  'Early Access to New Amenities',
  'Invitations to Exclusive Member Events',
  'Opportunities to Help Shape Future Improvements',
  'Priority Registration for New Experiences',
  'First Opportunity to Reserve New Cabins & Amenities',
];

const FUTURE = [
  'Third Cabin in 2027',
  'Hunt Cabins',
  'Primitive Camping',
  'Designated RV Sites',
  'Horse Stabling',
  'Expanded Private Horse Trail System',
  'Expanded Private ATV Trail System',
  'Additional Hunting Areas',
  'Sporting Clays',
  'Archery Range',
  'Disc Golf Course',
  'Outdoor Clinics',
  'Corporate Retreat Facility',
  'Exclusive Seasonal Member Events',
  'Outdoor Skills Workshops',
  'Fishing Improvements',
  'Wildlife Observation Areas',
  'Additional Recreation Areas',
  'Expanded Animal Programs & Experiences',
];

const BUSINESS_USES = [
  'Employee Appreciation',
  'Family Days',
  'Team Building',
  'Executive Retreats',
  'Company Picnics',
  'Client Entertainment',
];

export default function Landing({ onLogin }) {
  return (
    <div className="landing">
      <section className="lp-hero">
        <p className="lp-eyebrow">Membership Has Its Privileges</p>
        <h1>Become a Founding Member of Eagle Ridge Farm Outdoor Club</h1>
        <p className="lp-tagline">More Than a Membership. A Place to Belong.</p>
        <button className="primary" onClick={onLogin}>Member Sign In</button>
        <p className="lp-hero-note">Already invited? Sign in to manage your membership and reservations.</p>
      </section>

      <section className="lp-section lp-intro">
        <p>
          Nestled in the rolling hills of Corinth, Kentucky, conveniently located
          halfway between Cincinnati and Lexington, Eagle Ridge Farm Outdoor Club
          is a private outdoor club where families, friends, and organizations come
          together to enjoy recreation, relaxation, and unforgettable experiences.
        </p>
        <p>
          Whether you're looking for weekend adventures, a peaceful getaway, a place
          for your children to explore, or somewhere to reconnect with nature, you'll
          find it here.
        </p>
      </section>

      <img
        className="lp-showcase"
        src="/images/eagle-ridge-logo.webp"
        alt="Eagle Ridge Farm Outdoor Club logo over an aerial view of the farm."
        loading="lazy"
        width="1200"
        height="1200"
      />

      <section className="lp-section">
        <h2 className="lp-h2">Your Membership Experience</h2>
        <div className="lp-grid">
          {EXPERIENCE.map((col) => (
            <div className="lp-feature-card" key={col.title}>
              <h3><span className="lp-icon">{col.icon}</span>{col.title}</h3>
              <ul>
                {col.items.map((i) => <li key={i}>{i}</li>)}
              </ul>
            </div>
          ))}
          <div className="lp-feature-card lp-feature-wide">
            <h3><span className="lp-icon">🏡</span>Exclusive Cabin Access</h3>
            <p>Enjoy up to 30 nights each year in our fully furnished cabins.</p>
            <p>
              Eagle Ridge Farm currently features two private cabins, with a third
              cabin already planned as the club grows. Each cabin comfortably sleeps
              up to nine guests, making them perfect for family vacations, reunions,
              hunting weekends, employee appreciation retreats, or simply escaping
              everyday life.
            </p>
            <p>
              Your membership includes exclusive access to a peaceful retreat
              surrounded by hiking trails, campfires, fishing, and everything Eagle
              Ridge Farm has to offer.
            </p>
          </div>
          <div className="lp-feature-card lp-feature-wide">
            <h3><span className="lp-icon">🤝</span>Business Memberships</h3>
            <p>Give your employees and their families a benefit they'll actually use.</p>
            <p className="lp-subtle">Business memberships are ideal for:</p>
            <ul className="lp-inline-list">
              {BUSINESS_USES.map((i) => <li key={i}>{i}</li>)}
            </ul>
          </div>
        </div>
      </section>

      <section className="lp-section lp-band">
        <h2 className="lp-h2">Why Become a Founding Member?</h2>
        <p>
          As one of our original members, you'll help shape the future of Eagle Ridge
          Farm Outdoor Club while enjoying exclusive benefits.
        </p>
        <ul className="lp-check-list">
          {FOUNDING_PERKS.map((p) => <li key={p}>{p}</li>)}
        </ul>
      </section>

      <section className="lp-section lp-band">
        <h2 className="lp-h2">A Club Built by Its Members</h2>
        <p>
          Eagle Ridge Farm Outdoor Club isn't being built by a corporation—it's being
          built by people who believe time outdoors matters.
        </p>
        <p>
          Every Founding Membership helps preserve open space, expand opportunities,
          and create experiences that bring people together. Your membership directly
          supports the continued growth of Eagle Ridge Farm and helps shape the future
          of everything we're building.
        </p>
        <p>As a Founding Member, you'll always be part of the story that started it all.</p>
      </section>

      <PhotoGallery />

      <section className="lp-section">
        <h2 className="lp-h2">The Future We're Building</h2>
        <p>
          As Founding Membership grows, so will the Outdoor Club. Every new member
          helps shape what comes next. Our long-term vision includes:
        </p>
        <ul className="lp-future-list">
          {FUTURE.map((f) => <li key={f}>{f}</li>)}
        </ul>
        <p className="lp-subtle">...and much more.</p>
      </section>

      <Slideshow
        category="future-vision"
        title="Our Future Vision"
        blurb="A look at what Eagle Ridge Farm is growing into — shaped by our Founding Members."
      />

      <img
        className="lp-showcase lp-showcase-family"
        src="/images/fuller-family.webp"
        alt="The Fuller family standing together on a gravel lane at Eagle Ridge Farm."
        loading="lazy"
        width="803"
        height="1427"
      />

      <section className="lp-section lp-about">
        <h2 className="lp-h2">About Us</h2>
        <p>
          Hello! We're Zac and Katie Fuller, proud parents to two wonderful daughters,
          ages 4 and 1, and the family behind Eagle Ridge Farm.
        </p>
        <p>
          Like many families today, we've realized that meaningful time together doesn't
          happen by accident—it happens when we intentionally create opportunities to
          unplug, slow down, and reconnect. We prioritize time outdoors, limit screen time
          for our girls, and believe some of life's greatest lessons are learned on a trail,
          around a campfire, in the woods, or simply by exploring nature.
        </p>
        <p>
          One of our greatest joys is watching our daughters connect with other children
          outdoors. It's amazing to see their imaginations come alive as they build forts,
          move rocks, splash in creeks, chase butterflies, and invent games that don't
          require batteries or screens. We love seeing their creativity flourish, the
          friendships that naturally develop, and the sense of peace our family experiences
          when we're together outside. Those moments inspired us to create a place where
          other families can experience the same thing.
        </p>
        <p>
          Our family enjoys horseback riding, hunting, fishing, hiking, and spending as much
          time outdoors as possible. Zac is a former U.S. Army Green Beret who now owns and
          operates several businesses, while Katie is a former Intervention Specialist who now
          stays home with our daughters and helps support our family's ventures.
        </p>
        <p>
          We live in the Cincinnati area, but we intentionally built Eagle Ridge Farm in
          northern Kentucky as a place to slow down, reconnect with what matters most, and
          spend meaningful time together. We named the farm after the beautiful Eagle Creek
          that winds through the property—a place that quickly became the heart of our
          family's adventures.
        </p>
        <p>
          Owning and caring for a farm is both rewarding and challenging. We understand the
          commitment, effort, and expense that go into creating and maintaining a place like
          this. We also recognize that opportunities for families to consistently disconnect
          from technology and intentionally reconnect with nature are becoming increasingly
          rare. Not everyone has access to land, trails, or a place where they can simply slow
          down and enjoy the outdoors together.
        </p>
        <p>
          That's why we created the Eagle Ridge Farm Outdoor Club—to share the place that's
          brought our family so much joy and to invite others to build memories and traditions
          of their own.
        </p>
        <p>
          Our hope is to build more than a destination—we want to build a community. A place
          where families, friends, outdoor enthusiasts, and organizations can gather, create
          traditions, enjoy the outdoors, and experience the kind of connection that comes from
          spending intentional time together in nature.
        </p>
        <p>
          We'd be honored to welcome you to Eagle Ridge Farm and invite you to become part of
          the story we're building together.
        </p>
      </section>

      <InstagramFeed />

      <section className="lp-section lp-join">
        <h2 className="lp-h2">Join the Adventure</h2>
        <p>
          Whether you're looking for a place to spend weekends with your family, reconnect with
          friends, enjoy the outdoors, entertain employees or clients, or simply slow down and
          recharge, we'd love to welcome you.
        </p>

        <div className="lp-info-card">
          <h3>Founding Membership Information</h3>
          <p><strong>📍 Eagle Ridge Farm</strong><br />Corinth, Kentucky<br />Conveniently located halfway between Cincinnati and Lexington.</p>
          <p><strong>📅 Opening:</strong> October 2026</p>
          <p><strong>⭐ Limited Founding Memberships Available</strong></p>
        </div>

        <ApplicationForm />

        <p className="lp-signoff">Eagle Ridge Farm Outdoor Club<br /><em>Adventure is Better Together.</em></p>
      </section>
    </div>
  );
}
